import { z } from 'zod'
import type { AuditionVideo } from './intro-video.js'
import { MID_FLOW_VIDEO } from './intro-video.js'
import { type PriorityValue, priorityValueSchema } from './job-priorities.js'
import {
  MONTHS_ON_LINE,
  type TimelineValue,
  timelineValueSchema,
} from './job-timeline.js'
import { LEVEL_OPTIONS, MOVE_IDS } from './level-ladder.js'
import {
  OTHER_OPTION,
  otherAnswerName,
  otherTextSchema,
  settleOtherAnswers,
} from './other-answer.js'
import { type StoredSchema, WIDGETS } from './widgets.js'

/** What Contact says when there is neither a LinkedIn link nor the opt-out. */
export const LINKEDIN_MISSING_MESSAGE =
  "Add your LinkedIn profile, or tick that you don't have one."
/**
 * What Contact says when the link given is not a LinkedIn profile: the
 * feed, a company page, or somewhere else entirely. It names the shape to
 * paste, so the refusal is not the only clue.
 */
export const LINKEDIN_INVALID_MESSAGE =
  "That link doesn't look like a LinkedIn profile. Paste your profile's address, the one that starts with linkedin.com/in/."
/**
 * What Contact says when the link given is `/in/me/`, the shortcut the label
 * itself opens. It redirects to whoever is signed in, so stored as-is it would
 * show a reviewer their own profile.
 */
export const LINKEDIN_SHORTCUT_MESSAGE =
  "That's LinkedIn's shortcut to your own profile. Open it, then copy the address it lands on."
/** What Contact says until the terms and privacy policy are agreed. */
export const TERMS_MESSAGE =
  'Please agree to the terms and privacy policy to continue.'

/**
 * How long each Contact box may be, by field. The schema refuses past
 * these, and the boxes cap at them, so a long paste is trimmed rather than
 * refused in a sentence about something else. Email's cap is the address
 * length the mail standards allow.
 */
export const CONTACT_LIMITS = {
  firstName: 100,
  lastName: 100,
  email: 254,
  phone: 30,
  linkedInUrl: 300,
} as const

/** A typed link with its scheme, so the admin page can open it as-is. */
function withScheme(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/**
 * What kind of LinkedIn link a typed one is. A profile is `/in/<handle>`, or
 * the legacy `/pub/...` form, on LinkedIn or any subdomain of it, since
 * regional and mobile ones exist. The host is parsed rather than
 * substring-matched, so `linkedin.com.example.com` is not LinkedIn. The
 * `/in/me` shortcut is told apart from both, because it needs its own advice.
 */
function linkedInLinkKind(url: string): 'profile' | 'shortcut' | 'other' {
  let parsed: URL
  try {
    parsed = new URL(withScheme(url))
  } catch {
    return 'other'
  }
  const host = parsed.hostname.toLowerCase()
  if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) return 'other'
  const [section, handle] = parsed.pathname
    .toLowerCase()
    .split('/')
    .filter(Boolean)
  if (!handle) return 'other'
  if (section === 'pub') return 'profile'
  if (section !== 'in') return 'other'
  return handle === 'me' ? 'shortcut' : 'profile'
}

/**
 * What a LeetCode box says when the link given is not a LeetCode profile: a
 * problem page, the progress page, or somewhere else entirely. It names the
 * shape to paste and the way out, because the field is optional and a refusal
 * must never read as "you need one of these".
 */
export const LEETCODE_INVALID_MESSAGE =
  "That link doesn't look like a LeetCode profile. Paste your profile's address, the one that starts with leetcode.com/u/, or leave this blank."

/**
 * What a LeetCode box says when the link is longer than the column takes.
 * Reachable from a box that caps at the same number, because a link typed
 * without its scheme grows by `https://` on the way in.
 */
export const LEETCODE_TOO_LONG_MESSAGE =
  "That link is too long. Paste just your profile's address, with nothing after it."

/** How long a LeetCode link may be. The box caps at it, as Contact's do. */
export const LEETCODE_URL_LIMIT = 300

/**
 * First path segments on LeetCode that are pages, not people. Only consulted
 * for the legacy `leetcode.com/<handle>` form, where a handle and a page are
 * the same shape; `/u/<handle>` needs no list. Short on purpose: it names the
 * pages an applicant plausibly pastes from, not the whole site map.
 */
const LEETCODE_PAGES = new Set([
  'u',
  'problemset',
  'problems',
  'progress',
  'profile',
  'explore',
  'contest',
  'discuss',
  'studyplan',
  'submissions',
  'accounts',
  'subscribe',
])

/**
 * Whether a typed link is a LeetCode profile: `/u/<handle>`, with or without
 * a page of that profile after it, or the legacy bare `/<handle>`, on
 * leetcode.com or leetcode.cn (the mainland site keeps separate accounts).
 * The host is parsed, never substring-matched, for the reason
 * `linkedInLinkKind` gives.
 *
 * A deeper `/u/<handle>/…` is taken as given, as `linkedInLinkKind` takes
 * `/in/<handle>/…`: the `/u/` prefix has already said the segment after it
 * is a person, so the link names the applicant whatever page of theirs it
 * lands on, and refusing it would answer a link that does identify them
 * with the sentence saying it does not. How many segments there are only
 * decides the bare form, where a handle and a page are the same shape and
 * `LEETCODE_PAGES` has to break the tie.
 */
function isLeetcodeProfile(url: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(withScheme(url))
  } catch {
    return false
  }
  const host = parsed.hostname.toLowerCase()
  const onLeetcode = ['leetcode.com', 'leetcode.cn'].some(
    (site) => host === site || host.endsWith(`.${site}`),
  )
  if (!onLeetcode) return false
  const segments = parsed.pathname.toLowerCase().split('/').filter(Boolean)
  // `.at()`, so both are typed as the `undefined` they can be — `/u/` alone
  // has no second segment, and that check is what refuses it.
  const first = segments.at(0)
  const second = segments.at(1)
  if (first === 'u') return second !== undefined
  return (
    segments.length === 1 && first !== undefined && !LEETCODE_PAGES.has(first)
  )
}

/**
 * The LeetCode link, as every surface that takes one validates it: the
 * Background field through `saveStep` and the done screen's panel through
 * `saveLeetcode`. Optional everywhere, so `''` passes and means "no link" —
 * on a save it is the explicit take-down of one already shared. Anything
 * else must be a LeetCode profile and comes out with its scheme, so the admin
 * page never renders a link that goes nowhere.
 *
 * The cap is applied AFTER the scheme goes on, and so measures the string
 * that is actually stored. Capping only the input would make the schema
 * disagree with itself: the client parses what was typed and sends the
 * result, the server parses that result, and a scheme-less link near the
 * limit would pass on one side and come back "too big" from the other.
 */
export const leetcodeUrlSchema = z
  .string()
  .trim()
  .max(LEETCODE_URL_LIMIT, { error: LEETCODE_TOO_LONG_MESSAGE })
  .refine((url) => url === '' || isLeetcodeProfile(url), {
    error: LEETCODE_INVALID_MESSAGE,
  })
  .transform((url) => (url === '' ? '' : withScheme(url)))
  .pipe(
    z.string().max(LEETCODE_URL_LIMIT, { error: LEETCODE_TOO_LONG_MESSAGE }),
  )

/**
 * Contact-step contract, shared verbatim by the ContactStep form and the
 * `audition.start` procedure so client and server validation can never
 * drift (same pattern as stepSchema below for question steps). Owns the
 * email normalization and the empty-phone → undefined convention (the
 * router stores undefined as null).
 *
 * LinkedIn is required, with a real way out (LG-1030). Continuing takes a
 * link or the opt-out, and ticking the opt-out clears any link in the same
 * write, so no orphan URL survives beside "I don't have one". The terms must
 * be agreed. Marketing consent is separate and optional, because requiring
 * it in order to apply would make it no consent at all.
 */
export const contactSchema = z
  .object({
    email: z.string().trim().toLowerCase().max(CONTACT_LIMITS.email).email(),
    firstName: z.string().trim().min(1).max(CONTACT_LIMITS.firstName),
    lastName: z.string().trim().min(1).max(CONTACT_LIMITS.lastName),
    phone: z
      .string()
      .trim()
      .max(CONTACT_LIMITS.phone)
      .optional()
      .transform((v) => (v ? v : undefined)),
    linkedInUrl: z
      .string()
      .trim()
      .max(CONTACT_LIMITS.linkedInUrl)
      .optional()
      .transform((v) => (v ? v : undefined)),
    linkedInOptedOut: z.boolean().default(false),
    acceptedTerms: z.literal(true, { error: TERMS_MESSAGE }),
    marketingConsent: z.boolean().default(false),
  })
  .superRefine(
    (contact, ctx) => {
      if (contact.linkedInOptedOut) return
      if (!contact.linkedInUrl) {
        ctx.addIssue({
          code: 'custom',
          path: ['linkedInUrl'],
          message: LINKEDIN_MISSING_MESSAGE,
        })
        return
      }
      const kind = linkedInLinkKind(contact.linkedInUrl)
      if (kind !== 'profile')
        ctx.addIssue({
          code: 'custom',
          path: ['linkedInUrl'],
          message:
            kind === 'shortcut'
              ? LINKEDIN_SHORTCUT_MESSAGE
              : LINKEDIN_INVALID_MESSAGE,
        })
    },
    // Runs even when a field above has failed, so the applicant hears every
    // refusal on one press. The step records them then and only takes them
    // down afterwards; a sentence held back here would need a second press.
    // Only on an object, though: a body that never was one reaches the
    // refinement as itself, and reading a property off null would throw out
    // of safeParse instead of refusing.
    { when: ({ value }) => typeof value === 'object' && value !== null },
  )
  .transform(({ linkedInUrl, ...contact }) => ({
    ...contact,
    linkedInUrl:
      contact.linkedInOptedOut || !linkedInUrl
        ? undefined
        : withScheme(linkedInUrl),
  }))

/** The two asks Contact gained in LG-1030, by the key each refuses under. */
export type ContactOwed = 'linkedInUrl' | 'acceptedTerms'

/**
 * Which of LG-1030's asks a Contact still owes: the LinkedIn answer (a
 * link or the opt-out) and the terms. Judged on the stored shape, never by
 * re-parsing: the schema's output is not its own input (the scheme it adds
 * can carry a link at the cap past it), and a draft's other columns are
 * not what reopening Contact is about. The wizard uses this both to decide
 * that a resumed draft reopens on Contact and to say why, so the two can
 * never disagree. "No link" is null or undefined in a stored row and ''
 * in the wizard's form state; all three owe the link.
 */
export function contactStillOwed(contact: {
  linkedInUrl?: string | null
  linkedInOptedOut?: boolean
  acceptedTerms?: boolean
}): ContactOwed[] {
  const owed: ContactOwed[] = []
  if (!contact.linkedInOptedOut && !contact.linkedInUrl)
    owed.push('linkedInUrl')
  if (!contact.acceptedTerms) owed.push('acceptedTerms')
  return owed
}

/**
 * One stored answer. A field that stores a list stores an array, the job-search
 * timeline stores its three stretches as an object, the priority ladder stores
 * its two ordered piles as an object, and every other field stores a string.
 *
 * Widen this and the `saveStep` router input together — the router is the only
 * other place that has to name the shape, because everything else reads it
 * through this type.
 */
export type AuditionAnswer = string | string[] | TimelineValue | PriorityValue

/**
 * What every question carries, whatever widget asks it.
 *
 * The arms below add what only their own widget can mean. Splitting them is
 * what makes `options` required exactly where a schema is built from it — the
 * "must declare options" throw used to be a per-request 500 from inside
 * `stepSchema`, and is now unreachable by omission.
 */
interface BaseAuditionField {
  name: string
  label: string
  required: boolean
  placeholder?: string
  /** Sub-label rendered under the label — coaching on how to answer. */
  help?: string
  /**
   * Short label for the admin "At a glance" facts card (LG-414). Present on
   * the answers a reviewer scans rather than reads; absent fields render as
   * prose instead. Declared here so adding a question decides its own
   * treatment, rather than the admin page keeping a second list of field
   * names that silently rots when the wizard changes.
   */
  glance?: string
  /**
   * Which selection criterion this answer speaks to. The founders have not
   * settled on level vs motivation (LG-414 §3), so the admin queue offers a
   * lens toggle and this is what it highlights. Purely presentational — no
   * ranking is computed from it.
   */
  lens?: 'level' | 'motivation'
  /**
   * Names this question's answer was stored under before, for rows written
   * then. Read back for display only, never pre-filled: an answer stored
   * under an old name may not be a valid answer to today's question, so it
   * is shown to the reviewer and the applicant answers afresh. Declared on
   * the question so a later rename carries its history with it, Other text
   * included, rather than leaving a second list keyed by a name that no
   * longer exists.
   */
  formerNames?: readonly string[]
}

/**
 * The arms that ask for nothing extra spell their absent properties as
 * `never` rather than leaving them off. A union member that simply omits a
 * property makes that property unreadable on the union, and the registry's
 * descriptors take the union — so `field.multi` would stop compiling in the
 * one place that legitimately asks. Declared absent, it reads as `undefined`
 * everywhere it does not apply, and writing one is still an error.
 */
interface PlainField extends BaseAuditionField {
  options?: never
  multi?: never
  rungsFrom?: never
  other?: never
}

/** A written answer, one line or several. */
interface ProseField extends PlainField {
  type: 'text' | 'textarea'
}

/** A pick from a declared list of labels. */
interface ChipsField extends BaseAuditionField {
  type: 'chips'
  /** The labels themselves, and what the answer stores. */
  options: readonly string[]
  /** Accept more than one answer. */
  multi?: boolean
  rungsFrom?: never
  /**
   * What the box inside the Other chip asks, once Other is picked (LG-1030).
   * `label` is the box's accessible name. The text is stored beside this
   * answer under `otherAnswerName`. Declared exactly where `options` offers
   * Other; `other-answer.test.ts` holds that pairing.
   */
  other?: { label: string; placeholder: string }
}

/** The level ladder: which moves the applicant would actually accept. */
interface LadderField extends BaseAuditionField {
  type: 'ladder'
  /** The move ids it stores. */
  options: readonly string[]
  /**
   * The field whose answer names the rungs. Required so a ladder cannot be
   * added without saying what it is a ladder FROM — which would silently
   * downgrade every rung to a generic sub-label. It is a plain `string`, so
   * renaming the level question still compiles; `steps.test.ts` is what
   * catches that, and a literal union here is blocked (see LG-898 notes: `as
   * const satisfies` on `QUESTION_STEPS` produces 13 unrelated type errors).
   */
  rungsFrom: string
  /** Always multi-pick, so it never says so. */
  multi?: never
  other?: never
}

/** The job-search timeline, drawn as three stretches over a line of months. */
interface TimelineField extends PlainField {
  type: 'timeline'
}

/** The priority board: what the applicant is actually looking for. */
interface PrioritiesField extends PlainField {
  type: 'priorities'
}

/**
 * One question. `type` is the tag: everything that follows from it — the
 * empty value, the schema, the settling, how it reads back, what draws it —
 * is looked up in the widget registry rather than branched on here.
 */
export type AuditionField =
  | ProseField
  | ChipsField
  | LadderField
  | TimelineField
  | PrioritiesField

/**
 * Every `auditionSubmission` column an artifact may be stored in. The one
 * source for the union, so the surfaces that walk artifacts — hydration, the
 * write patch, the per-column schemas — enumerate them rather than naming one.
 */
export const ARTIFACT_COLUMNS = ['leetcodeUrl'] as const

/**
 * A box on a question step whose value is an ARTIFACT, not an answer: it is
 * stored in its own `auditionSubmission` column and never in the `answers`
 * jsonb (LG-1030's storage rule — artifacts and evidence go to columns).
 *
 * Deliberately not an `AuditionField`. Everything that walks `fields` — the
 * step schema, the recap, the admin answers card, the glance facts — reads
 * `answers`, and would either show this as a question nobody answered or
 * have to learn to skip it. Declared beside the fields instead, the artifact
 * is invisible to all of them, and the admin Evidence block goes on reading
 * the column it always did.
 */
export interface AuditionArtifact {
  /** The column it is stored in, and its key on the wire. */
  column: (typeof ARTIFACT_COLUMNS)[number]
  /** The field it is drawn after. Pinned to a real one in `steps.test.ts`. */
  after: string
  label: string
  /** How long the link may be. Its own, not the first column's. */
  limit: number
  /**
   * Where the label links. LeetCode has no self-redirect like LinkedIn's
   * `/in/me/`, so this can only be the site; `help` carries the way from
   * there to the profile address.
   */
  href: string
  help: string
  placeholder: string
}

export interface AuditionQuestionStep {
  id: string
  /**
   * Short user-facing name for the stage rail — two words at most, because
   * the rail lays every step out on one line. Distinct from `title`, which
   * is the full sentence heading above the fields. Required so a new
   * question step cannot reach the rail unnamed.
   */
  name: string
  title: string
  description?: string
  /**
   * Plays above this step's fields. Declared here so where the mid-flow
   * video sits is a data choice — moving it is this one property, with no
   * corresponding branch in the wizard to keep in step.
   */
  video?: AuditionVideo
  fields: readonly AuditionField[]
  /** Always optional: an artifact never holds Next by being blank. */
  artifacts?: readonly AuditionArtifact[]
}

/**
 * Question list and wording approved by Jon + Mike (2026-07-22 review).
 * Everything renders and validates from this array, so a copy change lands
 * as a data edit, not a code change. The word "audition" must never appear
 * in any string here — this is user-facing copy.
 */
export const QUESTION_STEPS: readonly AuditionQuestionStep[] = [
  {
    id: 'background',
    name: 'Background',
    title: 'Tell us where you are today',
    fields: [
      {
        name: 'currentRole',
        label: 'What best describes you right now?',
        type: 'chips',
        required: true,
        glance: 'Right now',
        options: [
          'Software engineer, employed',
          'Software engineer, between roles',
          'New grad / early career',
          'Career changer',
          OTHER_OPTION,
        ],
        other: {
          label: 'Tell us in a few words',
          placeholder:
            'Designer moving into engineering, returning after a break…',
        },
      },
      {
        name: 'currentLevel',
        label: 'What is your current level?',
        type: 'chips',
        required: true,
        glance: 'Level',
        lens: 'level',
        options: LEVEL_OPTIONS,
      },
      {
        name: 'targetLevel',
        label: 'What is your target level?',
        type: 'chips',
        multi: true,
        required: true,
        glance: 'Target',
        lens: 'level',
        options: LEVEL_OPTIONS,
      },
      {
        name: 'experience',
        label: 'Briefly, what is your engineering background?',
        type: 'textarea',
        required: true,
        placeholder: 'Years of experience, languages, the kind of work you do…',
      },
    ],
    artifacts: [
      {
        column: 'leetcodeUrl',
        // After the level questions, where the LG-898 canvas put it.
        after: 'targetLevel',
        label: 'LeetCode profile',
        limit: LEETCODE_URL_LIMIT,
        href: 'https://leetcode.com/',
        help: "If you have a LeetCode profile, we would love to see it. Plenty of people don't have one, and leaving this blank doesn't count against you in your application. We aren't scoring your profile. It just shows us what you have practised so far. Your profile's address looks like leetcode.com/u/your-handle. To find it, sign in to LeetCode, click your avatar in the top right, then click your name.",
        placeholder: 'leetcode.com/u/your-handle',
      },
    ],
  },
  {
    id: 'goals',
    name: 'Goals',
    title: 'Where do you want to be?',
    fields: [
      {
        name: 'jobPriorities',
        label: 'What are you actually looking for?',
        type: 'priorities',
        // Optional on purpose. Requiring it would make "your order happens to
        // be my order" unsayable — the only way to satisfy the gate is to
        // move a card, so a forced answer here is a worse answer than a
        // skipped one. See LG-1032's load budget: this question has to stay
        // cheap to remove, and that starts with it never blocking a submit.
        required: false,
        glance: 'Looking for',
        lens: 'motivation',
        help: 'Put what matters most at the top and drop anything you do not care about. There are no wrong answers, but be honest with yourself. Do not rank learning and growth first if what you really need is better compensation. We cannot help you get what you are after unless we know what it is.',
      },
      {
        name: 'openToMoves',
        label:
          'Given a compelling offer, what levels would you actually accept?',
        type: 'ladder',
        required: true,
        glance: 'Moves',
        rungsFrom: 'currentLevel',
        help: 'Some clients accept being down-levelled when it is a prestigious company or a great total compensation package. Others really value keeping their hard-earned title and would only accept a lateral move. And sometimes people are leaving an employer looking for a long-overdue level jump. Assuming the offer is compelling, tap the moves you would actually accept, and believe you would be qualified for.',
        options: MOVE_IDS,
      },
      {
        name: 'interviewTimeline',
        label: 'When do you expect to be interviewing?',
        type: 'timeline',
        required: true,
        glance: 'Interviewing',
        lens: 'level',
        // The limit is stated here, before anything is marked, and again in
        // the refusal when prep starts too late to fit inside it.
        help: `There is no right answer. It tells us where your head is at while we read the rest of your application. The whole plan has to fit within the next ${MONTHS_ON_LINE} months.`,
      },
      {
        name: 'oneYearWin',
        label:
          'If we were catching up a few months from today to celebrate, what would we be celebrating?',
        type: 'textarea',
        required: true,
        lens: 'motivation',
        help: 'Be specific about the offer, the role, the compensation level, and more. Paint us a picture!',
        placeholder: 'The offer, the company, the level, the life change…',
      },
      {
        name: 'sixtyDayFocus',
        label:
          'What are two to three things you most want to improve in the first 60 days?',
        type: 'textarea',
        required: true,
        help: 'Be honest — this is what we coach.',
        placeholder:
          'Dynamic programming? Communicating under pressure? Something else…',
      },
      {
        name: 'blockers',
        label: 'What has been holding you back so far?',
        type: 'textarea',
        required: true,
        placeholder:
          'Interviews that went sideways, prep that never stuck, time, confidence…',
      },
    ],
  },
  {
    id: 'motivation',
    name: 'Motivation',
    title: 'Why this program, why now?',
    video: MID_FLOW_VIDEO,
    fields: [
      {
        name: 'whyNow',
        label: 'Why are you applying now?',
        type: 'textarea',
        required: true,
        placeholder:
          "What's pushing you to move? Low total comp, a team you've outgrown, wanting to work somewhere better…",
      },
      {
        name: 'superpowers',
        label: 'What makes you stand out?',
        type: 'textarea',
        required: true,
        placeholder:
          "We don't take everybody. What makes you unique and valuable to this cohort?",
      },
      {
        // Chips since LG-1030. Rows written before hold free text under
        // `referral`: shown on read-back, never seeded into these chips.
        name: 'referralSource',
        formerNames: ['referral'],
        label: 'How did you hear about us?',
        type: 'chips',
        multi: true,
        required: false,
        glance: 'Heard via',
        help: 'Pick as many as apply.',
        options: [
          'YouTube',
          'Beyond Cracking the Coding Interview',
          'Interviewing.io',
          'Discord',
          'A friend or colleague',
          'Google',
          'LinkedIn',
          'A podcast',
          OTHER_OPTION,
        ],
        other: {
          label: 'Where did you hear about us?',
          placeholder: 'A newsletter, a conference talk…',
        },
      },
    ],
  },
]

/**
 * What a step does, so the wizard and the rail can find a step by an
 * explicit marker instead of by counting. `question` steps carry the id of
 * their `QUESTION_STEPS` entry; the other kinds are one-offs.
 */
export type AuditionStepKind = 'contact' | 'question' | 'account'

export interface AuditionWizardStep {
  id: string
  /** User-facing rail label. The word "audition" must never appear here. */
  name: string
  kind: AuditionStepKind
  /**
   * Video to play above this step's fields, if it declares one. The shell
   * reads it from here and from nowhere else, so `WIZARD_STEPS` stays the
   * single answer to "what does the wizard look like at step N".
   */
  video?: AuditionVideo
}

/**
 * Every wizard step, in order — the ONE place the wizard's shape is written
 * down. The stage rail's labels, `TOTAL_WIZARD_STEPS`, and every step index
 * below all read from this array, so adding a step renumbers the wizard and
 * relabels the rail in a single edit. A hand-maintained label array beside
 * it would be a second copy, and the second copy is the one that rots.
 */
export const WIZARD_STEPS: readonly AuditionWizardStep[] = [
  { id: 'contact', name: 'Contact', kind: 'contact' },
  ...QUESTION_STEPS.map(
    (step): AuditionWizardStep => ({
      id: step.id,
      name: step.name,
      kind: 'question',
      video: step.video,
    }),
  ),
  { id: 'account', name: 'Account', kind: 'account' },
]

export const TOTAL_WIZARD_STEPS = WIZARD_STEPS.length

/**
 * How long the whole application takes, printed wherever the flow sets that
 * expectation. It lives beside the step list it describes so that adding or
 * removing a question is the moment it gets re-derived, and every screen
 * prints the same number.
 *
 * Derived from the steps above for an applicant writing real answers: two
 * short videos (about four minutes), contact details and account sign-up
 * (about three), four chip questions, the ladder, the timeline and the
 * eight-item ranking (about five), and six essays at four to six minutes
 * each (about thirty). The essays dominate; a stopwatch on a fast typist
 * would undercount them.
 */
export const MINUTES_TO_APPLY = 40

/**
 * The server's `currentStep` as a 0-based index into `WIZARD_STEPS`.
 *
 * The two numbering schemes are a fact of the wire, not a choice: the column
 * is 1-based (the contact step is `currentStep` 1) because it is also what
 * the admin queue and the Customer.io funnel read, while the wizard indexes
 * the step list. This conversion used to sit inline at each of the three
 * places that cross the boundary, which is three chances to write `- 1`
 * where `+ 1` belongs.
 *
 * Clamped at BOTH ends, which the inline versions were not. They clamped
 * only the top, so a `currentStep` of 0 — absent, or a row written before
 * the column had a default — produced index -1, and `WIZARD_STEPS[-1]` is
 * undefined: a rail above an empty column, a silent dead end. The value comes
 * off the wire, so it does not get to be trusted.
 *
 * The top clamp is also what carries a draft across a step being REMOVED
 * (LG-1053 retired the standalone LeetCode step). A row parked on the old
 * last-but-one number now names the last step, and one parked past the end
 * is pulled back onto it, so a stored number always resolves to a real step.
 */
export function stepIndexFromCurrentStep(currentStep: number): number {
  return Math.min(Math.max(currentStep - 1, 0), TOTAL_WIZARD_STEPS - 1)
}

/**
 * A 0-based step index as the server's 1-based `currentStep`.
 *
 * The inverse of `stepIndexFromCurrentStep`, and the only other place the
 * two schemes meet.
 */
export function currentStepFromStepIndex(stepIndex: number): number {
  return Math.min(Math.max(stepIndex, 0), TOTAL_WIZARD_STEPS - 1) + 1
}

/**
 * Takes a plain `string`, not a literal id union, on purpose: the caller that
 * matters is `audition.saveStep`, which passes an unvalidated `stepId` off
 * the wire. Returning undefined for an unknown id IS the validation.
 */
export function getQuestionStep(id: string): AuditionQuestionStep | undefined {
  return QUESTION_STEPS.find((s) => s.id === id)
}

// Both parameters named on purpose. `ZodType` in zod 4 is
// `ZodType<Output = unknown, Input = unknown>`, so annotating the output
// alone erases the INPUT to `unknown` — and tRPC types a procedure's
// client-side argument from `z.input`, which is what `saveStep` records its
// `answers` with. Left at one parameter, `{ oneYearWin: 42 }` type-checked
// at the call site and the only feedback was the server's generic "Please
// complete the required fields."
const answerShapes = z.union([
  z.string(),
  z.array(z.string()),
  // The two object arms are disjoint by key and both `.strict()`, so neither
  // can swallow the other's shape; naming the ranking first keeps the error a
  // reader sees about the shape they actually sent.
  priorityValueSchema,
  timelineValueSchema,
])

/**
 * The compiler naming THIS line when a widget brings a new stored shape.
 *
 * `ZodType` is covariant in its output, so the annotation below does not fail
 * when an arm is missing — widening `AuditionAnswer` alone compiles clean. The
 * seventh widget would then draw, settle, and have every save rejected by
 * `saveStep` at runtime: exactly the "added an arm, missed a place, found out
 * in production" failure the widget registry exists to end, surviving in the
 * one place a `Record` over the type union cannot reach, because the wire
 * names shapes and not field types.
 *
 * Written unannotated above and checked here so the check sees the arms rather
 * than the annotation. The annotation itself stays: tRPC types a procedure's
 * client-side argument from `z.input`, and `saveStep` records `answers` with
 * this.
 */
const _everyAnswerShapeHasAnArm: AuditionAnswer extends z.output<
  typeof answerShapes
>
  ? true
  : 'auditionAnswerSchema is missing a z.union arm for a new AuditionAnswer shape' =
  true

export const auditionAnswerSchema: z.ZodType<AuditionAnswer, AuditionAnswer> =
  answerShapes

/**
 * One step's answers, as they validate.
 *
 * Every field asks its own widget for its schema, so a new widget cannot be
 * half-wired: the registry is a `Record` over the type union, and a type with
 * no descriptor is a compile error rather than a question that silently
 * accepts anything.
 */
export function stepSchema(
  step: AuditionQuestionStep,
): z.ZodType<
  Record<string, AuditionAnswer>,
  Record<string, AuditionAnswer | undefined>
> {
  const shape: Record<string, StoredSchema> = {}
  for (const field of step.fields) {
    shape[field.name] = WIDGETS[field.type].storedSchema(field)
    if (field.other) shape[otherAnswerName(field)] = otherTextSchema()
  }
  // Required fields are non-empty; optional ones transform undefined to '',
  // [] or an empty plan, so a declared key is always present. Other text that
  // outlived its chip is cleared on the way in, so the server never stores it.
  return z
    .object(shape)
    .strip()
    .transform((answers) => settleOtherAnswers(step, answers))
}

/** How each artifact column validates. A `Record`, so a new column has to say. */
const ARTIFACT_SCHEMAS: Record<
  AuditionArtifact['column'],
  z.ZodType<string, string>
> = {
  leetcodeUrl: leetcodeUrlSchema,
}

/**
 * A step's artifact values by column. `''` is "no link" — the take-down of
 * one already stored — and a key left out leaves its column alone.
 */
export type StepArtifacts = Partial<Record<AuditionArtifact['column'], string>>

/**
 * One step's artifacts, as they validate — `stepSchema`'s sibling, shared by
 * `QuestionStep` and `audition.saveStep` for the same reason.
 *
 * Strict, where `stepSchema` strips: an undeclared answer is dropped on the
 * floor, but an undeclared artifact names a COLUMN, and a step that never
 * asked for one does not get to write it.
 */
export function artifactsSchema(
  step: AuditionQuestionStep,
): z.ZodType<StepArtifacts, StepArtifacts> {
  const shape: Record<
    string,
    z.ZodType<string | undefined, string | undefined>
  > = {}
  for (const artifact of step.artifacts ?? [])
    shape[artifact.column] = ARTIFACT_SCHEMAS[artifact.column].optional()
  return z.object(shape).strict()
}

/**
 * The `auditionSubmission` patch a step's artifact values make. One place, so
 * every write says the same thing about a blank box: `''` is the take-down of
 * a stored link (null), and a column left out is left alone (drizzle skips
 * `undefined`).
 *
 * Walked from the columns rather than destructured by name, so declaring a
 * new artifact is the whole change. A write that hand-picked its column would
 * compile clean at every other layer and silently drop the value here.
 */
export function artifactPatch(
  artifacts: StepArtifacts,
): Partial<Record<AuditionArtifact['column'], string | null>> {
  const patch: Partial<Record<AuditionArtifact['column'], string | null>> = {}
  for (const column of ARTIFACT_COLUMNS) {
    const url = artifacts[column]
    if (url !== undefined) patch[column] = url || null
  }
  return patch
}

/**
 * The sentences a refused artifact is allowed to say to an applicant. Keyed
 * by column, so a new one has to bring its own rather than fall through to
 * the line that names no box.
 */
const ARTIFACT_MESSAGES_BY_COLUMN: Record<
  AuditionArtifact['column'],
  readonly string[]
> = {
  leetcodeUrl: [LEETCODE_INVALID_MESSAGE, LEETCODE_TOO_LONG_MESSAGE],
}
const ARTIFACT_MESSAGES: ReadonlySet<string> = new Set(
  Object.values(ARTIFACT_MESSAGES_BY_COLUMN).flat(),
)

/**
 * What to tell an applicant about a refused artifact. Only the sentences
 * authored above go back: `artifactsSchema` is `.strict()`, so a payload
 * naming a column the step never declared is refused with zod's own
 * "Unrecognized key", which describes the wire format and names nothing the
 * applicant could act on.
 */
export function artifactRefusalMessage(
  issues: readonly { message: string }[],
): string {
  for (const issue of issues)
    if (ARTIFACT_MESSAGES.has(issue.message)) return issue.message
  return 'Please check the links you added.'
}
